import { useGetFaireAcademyPageState } from "@faire/retailer-visitor-shared/hooks/openWithFaire/useGetFaireAcademyPageState";
import {
  CHECK_ANIMATION_MS,
  faireAcademyFadeIn,
} from "@faire/retailer-visitor-shared/views/open-with-faire/academy/guidePages/components/animationTimingConsts";
import { SignUpPrompt } from "@faire/retailer-visitor-shared/views/open-with-faire/academy/guidePages/components/ChapterPreview/components/SignUpPrompt";
import { useGuideStore } from "@faire/retailer-visitor-shared/views/open-with-faire/academy/guidePages/useGuideStore";
import type { FaireAcademyChapter } from "@faire/retailer-visitor-shared/views/open-with-faire/academy/types";
import { Button } from "@faire/web--source/slate/Button";
import { Column } from "@faire/web--source/slate/Layout";
import { responsiveMediaQueries } from "@faire/web--source/slate/Theme/responsiveMediaQueries";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import { trackOpenWithFaireIndividualGuideChapterCtaClick } from "@faire/web-api--source/events/openWithFaire/click/individualGuideChapterCta";
import React, { Suspense } from "react";
import { useLocation } from "react-router-dom";
import styled, { css } from "styled-components";

export const ChapterPreview: React.FC<{
  chapter: FaireAcademyChapter | undefined;
  shouldBlockContentPreview: boolean;
  isChapterRead?: boolean;
  isNextChapterPreview?: boolean;
}> = ({
  chapter,
  shouldBlockContentPreview,
  isChapterRead,
  isNextChapterPreview,
}) => {
  const [{ guide }] = useGuideStore(["guide"]);
  const { pageState } = useGetFaireAcademyPageState();
  const location = useLocation();

  const Chapter = chapter?.ChapterComponent;

  if (Chapter === undefined || chapter === undefined) {
    return null;
  }

  return (
    <>
      <ChapterContainer
        data-test-id="fa-chapter-preview"
        $fadeIn={isChapterRead}
      >
        <Suspense fallback={null}>
          <Chapter />
        </Suspense>
        <ChapterOverlay align="center" justify="flex-end">
          {shouldBlockContentPreview ? null : (
            <DynamicButton
              linkTo={{
                ...location,
                hash: chapter.titleHash,
              }}
              onClick={() => {
                trackOpenWithFaireIndividualGuideChapterCtaClick(
                  pageState,
                  guide.id,
                  chapter.id
                );
              }}
            >
              <StrictLocalMsg
                defaultMessage="Jump to the next chapter"
                description={{
                  text: "Go to the next chapter in a guide for opening a retailer business",
                  img: "https://cdn.faire.com/fastly/1fba0b2e920994166f60006abad95e304f9b25a491e7c38af4ab0434c7d4de5b.png",
                }}
              />
            </DynamicButton>
          )}
        </ChapterOverlay>
      </ChapterContainer>
      {shouldBlockContentPreview ? (
        <SignUpPrompt
          isNextChapterPreview={isNextChapterPreview}
          chapter={chapter}
        />
      ) : null}
    </>
  );
};

const DynamicButton = styled(Button)`
  ${responsiveMediaQueries.mobile} {
    width: 100%;
  }
`;

const ChapterContainer = styled(Column)<{ $fadeIn: boolean | undefined }>`
  position: relative;
  height: 440px;
  max-height: 440px;
  overflow: hidden;

  ${({ $fadeIn }) =>
    $fadeIn
      ? css`
          opacity: 0;
          animation: ${faireAcademyFadeIn} 1000ms forwards;
          animation-delay: ${CHECK_ANIMATION_MS}ms;
        `
      : null}
`;

const ChapterOverlay = styled(Column)`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(
      rgba(255, 255, 255, 0.5) 0%,
      rgba(255, 255, 255, 0.5) 100%
    ),
    linear-gradient(0deg, rgba(255, 255, 255, 1) 0%, rgba(255, 255, 255, 0) 85%);
`;
